import type { CSSProperties, ReactNode } from 'react';
import { useReveal } from '../hooks/useReveal';

interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Motion style on entry. */
  variant?: 'up' | 'scale';
  /** Stagger delay in ms. */
  delay?: number;
  /** Reveal once (default) or re-trigger on re-entry. */
  once?: boolean;
  style?: CSSProperties;
}

/**
 * Scroll-reveal wrapper. Applies the `.reveal` / `.reveal-scale` primitives
 * (see @byaft/ui/styles/base.css) and toggles `.is-visible` when scrolled into
 * view. Honors reduced-motion via CSS (content is always shown).
 */
export function Reveal({
  children,
  className = '',
  variant = 'up',
  delay = 0,
  once = true,
  style,
}: RevealProps) {
  const { ref, isVisible } = useReveal<HTMLDivElement>({ once });
  const base = variant === 'scale' ? 'reveal-scale' : 'reveal';

  return (
    <div
      ref={ref}
      className={`${base} ${isVisible ? 'is-visible' : ''} ${className}`}
      style={{ transitionDelay: delay ? `${delay}ms` : undefined, ...style }}
    >
      {children}
    </div>
  );
}
